import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { eyebrow, focusRing, h2Lg, pill, textLink } from "./styles";
import coverTrackers from "@/assets/blog/ai-portfolio-analysis-cover.jpg";
import coverBrokers from "@/assets/blog/brokers-3way-cover.jpg";
import coverCoastFire from "@/assets/blog/what-is-coast-fire-cover.jpg";

// Same cover files the blog uses for these posts (src/content/posts.ts COVERS map).
const POSTS = [
  { key: "trackers", href: "/blog/best-dividend-trackers-2026", cover: coverTrackers, w: 1600, h: 900 },
  {
    key: "brokers",
    href: "/blog/trading-212-vs-trade-republic-vs-interactive-brokers",
    cover: coverBrokers,
    w: 1536,
    h: 1024,
  },
  { key: "coastFire", href: "/blog/what-is-coast-fire", cover: coverCoastFire, w: 1600, h: 900 },
] as const;

const TOOLS = [
  { key: "dividend", href: "/tools/dividend-calculator" },
  { key: "retirement", href: "/tools/retirement-calculator" },
  { key: "coastFire", href: "/tools/coast-fire-calculator" },
  { key: "rentVsBuy", href: "/tools/rent-vs-buy-calculator" },
  { key: "dividendenRechner", href: "/tools/dividenden-rechner", lang: "de" },
] as const;

const GUIDES = [
  { key: "halal", href: "/halal-investing" },
  { key: "retire50", href: "/blog/how-much-to-retire-at-50" },
  { key: "retire55", href: "/blog/how-much-to-retire-at-55" },
  { key: "retire60", href: "/blog/how-much-to-retire-at-60" },
  { key: "retire67", href: "/blog/how-much-to-retire-at-67" },
] as const;

const Arrow = () => <ArrowRight aria-hidden="true" className="h-4 w-4 rtl:-scale-x-100" />;

const LearnPlan = () => {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="learn-h" className="bg-white px-[22px] py-[clamp(88px,9vw,128px)]">
      <div className="mx-auto max-w-[1200px]">
        <div className="pf-rv flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-[720px]">
            <p className={eyebrow}>{t("home.learn.eyebrow")}</p>
            <h2 id="learn-h" className={h2Lg}>
              {t("home.learn.title")}
            </h2>
          </div>
          <Link to="/blog" className={`${textLink} text-[17px]`}>
            {t("home.learn.allArticles")}
            <Arrow />
          </Link>
        </div>

        <ul className="pf-rv2 m-0 mt-14 grid list-none grid-cols-1 gap-6 p-0 md:grid-cols-2 lg:grid-cols-3">
          {POSTS.map((post) => (
            <li key={post.key}>
              <Link to={post.href} className={`pf-card group block rounded-[20px] text-foreground ${focusRing}`}>
                <span className="block aspect-video overflow-hidden rounded-[20px] bg-pf-sage-soft">
                  <img
                    src={post.cover}
                    alt=""
                    width={post.w}
                    height={post.h}
                    loading="lazy"
                    decoding="async"
                    className="block h-full w-full object-cover"
                  />
                </span>
                <span className="mt-[18px] block text-xs font-semibold uppercase tracking-[0.08em] text-pf-ink2">
                  {t(`home.learn.posts.${post.key}.tag`)}
                </span>
                <span className="mt-1.5 block text-[21px] font-semibold leading-[1.25] tracking-[-0.02em] group-hover:underline">
                  {t(`home.learn.posts.${post.key}.title`)}
                </span>
              </Link>
            </li>
          ))}
        </ul>

        <div className="pf-rv2 mt-[72px] grid grid-cols-1 gap-7 border-t border-foreground/10 pt-10 md:grid-cols-2 md:gap-10">
          <div>
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-[21px] font-semibold tracking-[-0.015em]">{t("home.learn.toolsTitle")}</h3>
              <Link to="/tools" className={`${textLink} text-[15px]`}>
                {t("home.learn.allTools")}
                <Arrow />
              </Link>
            </div>
            <ul className="m-0 mt-4 flex list-none flex-wrap gap-2.5 p-0">
              {TOOLS.map((tool) => (
                <li key={tool.key}>
                  <Link to={tool.href} className={pill} lang={"lang" in tool ? tool.lang : undefined}>
                    {t(`home.learn.tools.${tool.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="flex min-h-[44px] items-center text-[21px] font-semibold tracking-[-0.015em]">
              {t("home.learn.guidesTitle")}
            </h3>
            <ul className="m-0 mt-4 flex list-none flex-wrap gap-2.5 p-0">
              {GUIDES.map((guide) => (
                <li key={guide.key}>
                  <Link to={guide.href} className={`${pill} tabular-nums`}>
                    {t(`home.learn.guides.${guide.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LearnPlan;
